import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import * as stylex from "@stylexjs/stylex";
import { queries } from "../lib/api";
import { historySearchSchema } from "../lib/schemas";
import { PageHeader, SearchField, ResultStatus, TableScroll } from "../components/Workspace";
import { RequestFailure } from "../components/RequestFailure";
import { HistoryControls } from "../components/HistoryControls";
import { styles } from "../lib/styles";

export const Route = createFileRoute("/audit")({
  validateSearch: historySearchSchema,
  component: ActivityPage,
});
function ActivityPage() {
  const filters = Route.useSearch(),
    navigate = Route.useNavigate();
  const me = useQuery(queries.me());
  const list = useQuery({
    ...queries.events({ q: filters.q, action: filters.action, cursor: filters.cursor }),
    enabled: me.data?.roles.includes("auditor") === true,
  });
  const change = (value: Partial<typeof filters>) => {
    void navigate({ search: (current) => ({ ...current, ...value }), replace: true });
  };
  if (me.isPending) return <p aria-busy="true">Checking activity permissions…</p>;
  if (!me.data?.roles.includes("auditor"))
    return (
      <p role="alert" {...stylex.props(styles.error)}>
        Auditor access is required to view activity.
      </p>
    );
  return (
    <>
      <PageHeader
        title="Activity"
        description="Search access requests, administrative changes and authorization results across the full audit history."
      />
      <RequestFailure
        error={list.error}
        retry={() => {
          void list.refetch();
        }}
        pending={list.isFetching}
      />
      <section data-workspace-panel {...stylex.props(styles.panel)}>
        <div className="table-toolbar">
          <SearchField
            label="Search activity"
            value={filters.q ?? ""}
            onChange={(q) => change({ q, cursor: undefined })}
            placeholder="Search identity, target or details…"
          />
          <label {...stylex.props(styles.field)}>
            Action
            <select
              {...stylex.props(styles.select)}
              value={filters.action ?? "all"}
              onChange={(event) => change({ action: event.target.value, cursor: undefined })}
            >
              <option value="all">All actions</option>
              {(list.data?.actions ?? []).map((value) => (
                <option key={value} value={value}>
                  {value.replaceAll("_", " ")}
                </option>
              ))}
            </select>
          </label>
        </div>
        <TableScroll label="Activity table">
          <table {...stylex.props(styles.table)}>
            <thead>
              <tr>
                {["When", "Identity", "Action", "Target", "Details"].map((heading) => (
                  <th key={heading} {...stylex.props(styles.th)}>
                    {heading}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {list.data?.items.map((event) => (
                <tr key={event.id}>
                  {[
                    new Date(event.at).toLocaleString(),
                    event.actor,
                    event.action,
                    event.target,
                    event.detail,
                  ].map((value, index) => (
                    <td key={index} {...stylex.props(styles.td)}>
                      {value}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </TableScroll>
        {list.isPending && !list.error && (
          <p aria-busy="true" {...stylex.props(styles.empty)}>
            Loading activity…
          </p>
        )}
        {list.isSuccess && !list.isFetching && list.data.items.length === 0 && (
          <ResultStatus>No matching activity. Try another search or action filter.</ResultStatus>
        )}
        <HistoryControls
          count={list.data?.items.length}
          noun="events"
          next={list.data?.page.next_cursor}
          previous={list.data?.page.previous_cursor}
          cursor={filters.cursor}
          busy={list.isFetching}
          onPage={(cursor) => change({ cursor })}
        />
      </section>
    </>
  );
}
